import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { AgGridReact } from "ag-grid-react";
import SearchBar from "../../../../SearchBarTable/SearchBarTable";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
// @ts-ignore CSS is loaded by the application bundler.
import "../../../AgGridTheme.css";
import SortHeader from "../../../SortingHeader";
import { FaArrowUp, FaFilter, FaGlobe, FaSearch, FaUserCheck } from "react-icons/fa";

import {
  ClientSideRowModelModule,
  ModuleRegistry,
  TextFilterModule,
  PaginationModule,
} from "ag-grid-community";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  PaginationModule,
]);


interface UsersTableProps {
  users: any[];
  identityActivities: any[];
  companies: any[];
  selectedRow: number | null;
  onRowSelect: (user: any) => void;
  onClearSelection: () => void;
  // entityType: "USER" | "SERVICE_PRINCIPAL";
  // onEntityTypeChange: () => void;
}

const CompanyFilterHeader = (props: any) => {
  const {
    column,
    displayName,
    progressSort,
    companyOptions,
    visibleCompanies,
    onVisibleCompaniesChange,
  } = props;
  const [sort, setSort] = useState(column.getSort());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
  const [filterHeight, setFilterHeight] = useState(360);

  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort());
    column.addEventListener("sortChanged", onSortChanged);
    return () => column.removeEventListener("sortChanged", onSortChanged);
  }, [column]);

  useEffect(() => {
    if (!isFilterOpen) return;

    const closeFilter = (event: MouseEvent) => {
      if (
        !filterButtonRef.current?.contains(event.target as Node) &&
        !filterPanelRef.current?.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", closeFilter);
    return () => document.removeEventListener("mousedown", closeFilter);
  }, [isFilterOpen]);

  const matchingCompanies = companyOptions.filter((companyName: string) =>
    companyName.toLowerCase().includes(companySearch.toLowerCase())
  );
  const allMatchingCompaniesSelected =
    matchingCompanies.length > 0 &&
    matchingCompanies.every((companyName: string) =>
      selectedCompanies.includes(companyName)
    );

  const openFilter = () => {
    const filterButtonBounds = filterButtonRef.current?.getBoundingClientRect();
    if (filterButtonBounds) {
      const panelHeight = Math.min(360, window.innerHeight - 16);
      const spaceBelow = window.innerHeight - filterButtonBounds.bottom - 14;
      const spaceAbove = filterButtonBounds.top - 14;
      const openAbove = spaceBelow < panelHeight && spaceAbove > spaceBelow;
      const availableHeight = Math.max(0, Math.min(panelHeight, openAbove ? spaceAbove : spaceBelow));
      const top = openAbove
        ? filterButtonBounds.top - availableHeight - 6
        : filterButtonBounds.bottom + 6;

      setFilterPosition({
        top,
        left: Math.max(8, filterButtonBounds.right - 264),
      });
      setFilterHeight(availableHeight);
    }
    setSelectedCompanies(visibleCompanies ?? []);
    setCompanySearch("");
    setIsFilterOpen(true);
  };

  const toggleCompany = (companyName: string) => {
    setSelectedCompanies((currentCompanies) =>
      currentCompanies.includes(companyName)
        ? currentCompanies.filter((company) => company !== companyName)
        : [...currentCompanies, companyName]
    );
  };

  const toggleAllMatchingCompanies = () => {
    setSelectedCompanies((currentCompanies) =>
      allMatchingCompaniesSelected
        ? currentCompanies.filter((company) => !matchingCompanies.includes(company))
        : Array.from(new Set([...currentCompanies, ...matchingCompanies]))
    );
  };

  return (
    <div className="flex h-full items-center gap-2">
      <button
        type="button"
        onClick={() => progressSort()}
        className="flex min-w-0 flex-1 items-center gap-1 text-left"
        title="Sort by company name"
      >
        <span className="flex min-w-0 items-center gap-2">
          <FaGlobe className="text-gray-600" />
          <span className="truncate">{displayName}</span>
        </span>
        <FaArrowUp
          className={`shrink-0 text-gray-600 transition-transform duration-200 ${
            sort === "desc" ? "rotate-180" : ""
          } ${!sort ? "opacity-30" : ""}`}
        />
      </button>
      <button
        ref={filterButtonRef}
        type="button"
        aria-label="Filter companies to hide"
        aria-expanded={isFilterOpen}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={openFilter}
        className={`flex h-8 shrink-0 items-center gap-1 rounded-md border px-2 text-xs font-medium shadow-sm transition-colors ${
          visibleCompanies !== null
            ? "border-[#4F46E5] bg-[#EEF2FF] text-[#4338CA]"
            : "border-[#C7D2FE] bg-[#F8FAFC] text-slate-600 hover:border-[#818CF8]"
        }`}
      >
        <FaFilter />
        {visibleCompanies !== null
          ? `${companyOptions.length - visibleCompanies.length} hidden`
          : "Filter"}
      </button>
      {isFilterOpen &&
        createPortal(
          <div
            ref={filterPanelRef}
            className="fixed z-[10000] flex w-64 flex-col rounded-lg border border-slate-200 bg-white p-3 shadow-xl"
            style={{ top: filterPosition.top, left: filterPosition.left, height: filterHeight }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="relative">
              <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                autoFocus
                value={companySearch}
                onChange={(event) => setCompanySearch(event.target.value)}
                placeholder="Search companies..."
                className="h-9 w-full rounded-md border border-slate-300 py-1 pl-8 pr-2 text-sm font-normal text-slate-700 outline-none focus:border-[#4F46E5]"
              />
            </div>
            <p className="mt-2 text-xs font-normal text-slate-500">
              Select the companies to show. Unchecked companies are hidden.
            </p>
            <label className="mt-3 flex cursor-pointer items-center gap-2 border-b border-slate-200 pb-2 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={allMatchingCompaniesSelected}
                onChange={toggleAllMatchingCompanies}
                className="h-4 w-4 accent-[#4F46E5]"
              />
              Show all companies
            </label>
            <div className="min-h-0 flex-1 overflow-y-auto py-2">
              {matchingCompanies.map((companyName: string) => (
                <label key={companyName} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 text-sm font-normal text-slate-700 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(companyName)}
                    onChange={() => toggleCompany(companyName)}
                    className="h-4 w-4 accent-[#4F46E5]"
                  />
                  <span className="truncate">{companyName}</span>
                </label>
              ))}
              {matchingCompanies.length === 0 && (
                <p className="px-1 py-3 text-sm font-normal text-slate-500">No companies found.</p>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-between border-t border-slate-200 pt-3">
              <button
                type="button"
                onClick={() => setSelectedCompanies([])}
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                Hide all
              </button>
              <button
                type="button"
                onClick={() => {
                  onVisibleCompaniesChange(selectedCompanies);
                  setIsFilterOpen(false);
                }}
                className="rounded-md bg-[#4F46E5] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#4338CA]"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  identityActivities,
  companies,
  selectedRow,
  onRowSelect,
  // entityType,
  // onEntityTypeChange,
}) => {
  
  const gridRef = useRef<AgGridReact>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [visibleCompanies, setVisibleCompanies] = useState<string[] | null>(null);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };
  const companyMap = useMemo(() => {
    const map: Record<number, string> = {};
    companies.forEach((c) => {
      map[c.company_id] = c.company_name;
    });
    return map;
  }, [companies]);
  const companyOptions = useMemo(
    () => Array.from(new Set(Object.values(companyMap))).sort(),
    [companyMap]
  );

  const getStatusLabel = (enabled: string) => {
    return enabled === "Y" ? "Enabled" : "Disabled";
  };

  const formatDate = (value: any) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
  };

  const getCreationDate = (user: any) =>
    user.user_creation_date || user.created_date || user.sys_created_date;
  

  const filteredUsers = useMemo(() => {
    const lowerSearch = searchQuery.toLowerCase();
    const activitiesByIdentity = new Map(
      identityActivities.map((activity) => [
        activity.identity_id?.toLowerCase(),
        activity,
      ])
    );
  
    const matchingUsers = users
      .map((user) => {
        const identityId =
          user.is_service_principal === "Y"
            ? user.client_id || user.entra_object_id
            : user.entra_object_id;
        const activity = identityId
          ? activitiesByIdentity.get(identityId.toLowerCase())
          : undefined;

        return {
          ...user,
          activity,
          company_name: companyMap[user.company_id] || "—",
        };
      })
      .filter((user) =>
        (user.login_name?.toLowerCase().includes(lowerSearch) ||
          user.company_name?.toLowerCase().includes(lowerSearch)) &&
        (visibleCompanies === null ||
          visibleCompanies.some(
            (companyName) =>
              user.company_name?.toLowerCase() === companyName.toLowerCase()
          ))
      );

    if (!isReviewMode) return matchingUsers;

    return matchingUsers
      .filter((user) => user.enabled === "Y")
      .sort((first, second) => {
      const firstLastActive = new Date(first.activity?.last_active_utc || 0).getTime();
      const secondLastActive = new Date(second.activity?.last_active_utc || 0).getTime();
      return firstLastActive - secondLastActive;
      });
  }, [users, identityActivities, searchQuery, companyMap, visibleCompanies, isReviewMode]);
  
  
  const columnDefs = useMemo(
  () => [
    {
      headerName: "Name",
      field: "login_name",
      sortable: true,
      flex: 1,
      minWidth: 220,
      headerComponent: SortHeader,
      headerComponentParams: {
        displayName: "Name",
        enableSorting: true,
      },
      headerClass:
        "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
    },
    {
      headerName: "Company Name",
      field: "company_name",
      sortable: true,
      flex: 1,
      minWidth: 290,
      headerComponent: CompanyFilterHeader,
      headerComponentParams: {
        displayName: "Company Name",
        companyOptions,
        visibleCompanies,
        onVisibleCompaniesChange: setVisibleCompanies,
      },
      headerClass:
        "px-3 py-1 text-left text-base font-bold text-gray-700 bg-gray-200",
    },
    {
      headerName: "Status",
      field: "enabled",
      sortable: true,
      flex: 1,
      minWidth: 160,
      headerComponent: SortHeader,
      headerComponentParams: {
        displayName: "Status",
        enableSorting: true,
      },
      valueFormatter: (params: any) => getStatusLabel(params.value),
      cellClass: (params: any) =>
        params.value === "Y" ? "text-green-600 font-semibold" : "text-red-600 font-semibold",
      headerClass:
        "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
    },
    {
      headerName: "Last Active",
      sortable: true,
      flex: 1,
      minWidth: 150,
      valueGetter: (params: any) => params.data.activity?.last_active_utc,
      valueFormatter: (params: any) => formatDate(params.value),
      headerComponent: SortHeader,
      headerComponentParams: { displayName: "Last Active", enableSorting: true },
      headerClass:
        "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
    },
    {
      headerName: "Created",
      sortable: true,
      flex: 1,
      minWidth: 150,
      valueGetter: (params: any) => getCreationDate(params.data),
      valueFormatter: (params: any) => formatDate(params.value),
      headerComponent: SortHeader,
      headerComponentParams: { displayName: "Created", enableSorting: true },
      headerClass:
        "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer",
    },
  ],
  [companyOptions, visibleCompanies]
);


  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      cellStyle: {
        padding: "8px 12px",
        fontSize: "16px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
      },
    }),
    []
  );

  const handleRowClicked = useCallback(
    (event: any) => {
      onRowSelect(event.data);
    },
    [onRowSelect]
  );

  useEffect(() => {
    if (gridRef.current && selectedRow !== null) {
      gridRef.current.api.forEachNode((node) => {
        node.setSelected(node.data.user_id === selectedRow);
      });
    }
  }, [selectedRow]);

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins">
      {/* <div className="flex justify-between items-center mb-4">
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            placeholder="Search Users..."
          />
        </div>
      </div> */}
      <div className="flex justify-between items-center mb-4">

        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full max-w-md">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            placeholder="Search Users..."
          />
        </div>
        <button
          type="button"
          onClick={() => setIsReviewMode((previous) => !previous)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-300 ${
            isReviewMode
              ? "bg-red-500 text-white hover:bg-red-600"
              : "bg-[#a6a9ac] text-white hover:bg-[#474747]"
          }`}
        >
          <FaUserCheck />
          {isReviewMode ? "Exit Review" : "User Access Review"}
        </button>
      </div>

      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700"
        style={{
          height: "var(--admin-tab-grid-height, max(475px, calc(100dvh - 320px)))",
          width: "100%",
          overflow: "auto",
          border: "1px solid #E7E4FF",
          borderRadius: "8px",
        }}
      >
        <AgGridReact
          theme="legacy"
          ref={gridRef}
          rowData={filteredUsers}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={{ mode: 'singleRow' }}
          onRowClicked={handleRowClicked}
          domLayout="normal"
          pagination={true}
          rowHeight={60}
          headerHeight={55}
          suppressRowHoverHighlight={false}
          getRowClass={(params) =>
            params.data?.user_id === selectedRow ? "selected-row" : ""
          }
        />
      </div>
    </div>
  );
};

export default UsersTable;
